/**
 * WebSocket Test Page
 * Test AliceBlue WebSocket connection and depth data (TBQ/TSQ)
 */

import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import api from '../services/api';

const WebSocketTest = () => {
  const { userId, userSession } = useSelector((state) => state.auth);
  const [logs, setLogs] = useState([]);
  const [wsSession, setWsSession] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef(null);
  const logContainerRef = useRef(null);
  
  // NFO symbol selection states
  const [symbolName, setSymbolName] = useState('NIFTY');
  const [availableExpiries, setAvailableExpiries] = useState([]);
  const [selectedExpiry, setSelectedExpiry] = useState('');
  const [strikePrice, setStrikePrice] = useState('26000');
  const [testSymbols, setTestSymbols] = useState([]);
  const [depthData, setDepthData] = useState({});

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs((prev) => [...prev, { timestamp, message, type }]);
  };

  // Auto-scroll logs to bottom
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  // Component mount log
  useEffect(() => {
    addLog('🚀 WebSocket Test Page loaded', 'info');
    if (userId) {
      addLog(`👤 User: ${userId}`, 'info');
    }
  }, []);

  // Fetch available expiry dates when symbol changes
  useEffect(() => {
    const fetchExpiries = async () => {
      if (!symbolName || !userSession) return;
      
      try {
        addLog(`🔍 Fetching expiries for ${symbolName}...`, 'info');
        const response = await api.get(
          `/symbol-filters/expiry-dates?symbolName=${symbolName}&exchange=NFO`,
          { headers: { Authorization: `Bearer ${userSession}` } }
        );
        
        if (response.data.success) {
          setAvailableExpiries(response.data.data);
          if (response.data.data.length > 0) {
            setSelectedExpiry(response.data.data[0].date);
            addLog(`✅ Found ${response.data.data.length} expiry dates`, 'success');
          } else {
            addLog('⚠️ No expiry dates found', 'warning');
          }
        }
      } catch (error) {
        addLog(`❌ Error fetching expiries: ${error.message}`, 'error');
        console.error('Error fetching expiries:', error);
      }
    };
    
    fetchExpiries();
  }, [symbolName, userSession]);

  // Fetch symbols when expiry or strike changes
  const fetchTestSymbols = async () => {
    if (!symbolName || !selectedExpiry || !strikePrice || !userSession) {
      addLog('❌ Please select symbol, expiry and strike price', 'error');
      return;
    }
    
    try {
      addLog(`🔍 Fetching symbols for ${symbolName} ${selectedExpiry} ${strikePrice}...`, 'info');
      
      const ceResponse = await api.get(
        `/symbol-filters/search?symbolName=${symbolName}&expiryDate=${selectedExpiry}&optionType=CE&strikePrice=${strikePrice}&exchange=NFO`,
        { headers: { Authorization: `Bearer ${userSession}` } }
      );
      
      const peResponse = await api.get(
        `/symbol-filters/search?symbolName=${symbolName}&expiryDate=${selectedExpiry}&optionType=PE&strikePrice=${strikePrice}&exchange=NFO`,
        { headers: { Authorization: `Bearer ${userSession}` } }
      );
      
      const symbols = [];
      
      if (ceResponse.data.success && ceResponse.data.data.length > 0) {
        const ceData = ceResponse.data.data[0];
        symbols.push({
          exchange: 'NFO',
          token: ceData.instrumentId,
          symbol: ceData.tradingSymbol,
          type: 'CE'
        });
      }
      
      if (peResponse.data.success && peResponse.data.data.length > 0) {
        const peData = peResponse.data.data[0];
        symbols.push({
          exchange: 'NFO',
          token: peData.instrumentId,
          symbol: peData.tradingSymbol,
          type: 'PE'
        });
      }
      
      setTestSymbols(symbols);
      addLog(`✅ Found ${symbols.length} symbols`, 'success');
      symbols.forEach(s => addLog(`  ${s.symbol} - ${s.exchange}|${s.token}`, 'info'));
      
    } catch (error) {
      addLog(`❌ Error fetching symbols: ${error.message}`, 'error');
    }
  };

  useEffect(() => {
    // Auto-scroll to bottom when new logs are added
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  const createWsSession = async () => {
    try {
      addLog('Creating WebSocket session (using OAuth userSession)...', 'info');
      
      // Get userSession from localStorage or sessionStorage
      const userSession = localStorage.getItem('userSession') || sessionStorage.getItem('userSession');
      if (!userSession) {
        addLog('❌ No user session found. Please login first.', 'error');
        addLog('Hint: Make sure you logged in via the Login page first', 'warning');
        return null;
      }
      
      addLog('✅ Found userSession, creating WebSocket session...', 'info');
      
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
      const response = await fetch(`${API_BASE_URL}/api/auth/ws-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userSession }),
      });

      const data = await response.json();
      
      if (data.success && data.data?.wsSess) {
        setWsSession(data.data.wsSess);
        addLog(`✅ WebSocket session created: ${data.data.wsSess}`, 'success');
        return data.data.wsSess;
      } else {
        addLog(`❌ Failed to create WS session: ${data.error}`, 'error');
        if (response.status === 405 || data.error.includes('405')) {
          addLog('', 'info');
          addLog('⚠️  AliceBlue WebSocket Not Enabled', 'warning');
          addLog('════════════════════════════════════════', 'warning');
          addLog('AliceBlue\'s real-time WebSocket streaming requires:', 'warning');
          addLog('  • A paid subscription plan', 'warning');
          addLog('  • Additional permissions from AliceBlue', 'warning');
          addLog('', 'info');
          addLog('Contact: support@aliceblueonline.com', 'warning');
          addLog('Or visit: https://ant.aliceblueonline.com/apps', 'warning');
          addLog('════════════════════════════════════════', 'warning');
        }
        return null;
      }
    } catch (error) {
      addLog(`❌ Error creating WS session: ${error.message}`, 'error');
      return null;
    }
  };

  const generateAuthToken = (wsSess) => {
    // Double SHA-256 encryption
    const encoder = new TextEncoder();
    
    return crypto.subtle.digest('SHA-256', encoder.encode(wsSess))
      .then(hash1 => {
        const hex1 = Array.from(new Uint8Array(hash1))
          .map(b => b.toString(16).padStart(2, '0'))
          .join('');
        return crypto.subtle.digest('SHA-256', encoder.encode(hex1));
      })
      .then(hash2 => {
        const hex2 = Array.from(new Uint8Array(hash2))
          .map(b => b.toString(16).padStart(2, '0'))
          .join('');
        return hex2;
      });
  };

  const connectWebSocket = async () => {
    try {
      // Step 1: Create WS Session
      let session = wsSession;
      if (!session) {
        session = await createWsSession();
        if (!session) return;
      }

      // Step 2: Generate Auth Token
      addLog('Generating authentication token...', 'info');
      const susertoken = await generateAuthToken(session);
      addLog(`✅ Auth token generated: ${susertoken.substring(0, 20)}...`, 'success');

      // Step 3: Connect to WebSocket
      addLog('Connecting to WebSocket...', 'info');
      const ws = new WebSocket('wss://ws2.aliceblueonline.com/NorenWS/');
      wsRef.current = ws;

      ws.onopen = () => {
        addLog('✅ WebSocket connected', 'success');
        
        // Step 4: Authenticate
        const authMessage = {
          susertoken: susertoken,
          t: 'c',
          actid: `${userId}_API`,
          uid: `${userId}_API`,
          source: 'API'
        };
        
        addLog('Sending authentication...', 'info');
        ws.send(JSON.stringify(authMessage));
      };

      ws.onmessage = (event) => {
        const message = JSON.parse(event.data);
        
        // Connection acknowledgment
        if (message.t === 'ck') {
          if (message.s === 'OK') {
            addLog('✅ Authentication successful!', 'success');
            setIsConnected(true);
          } else {
            addLog('❌ Authentication failed', 'error');
          }
        }

        // Tick acknowledgment
        if (message.t === 'tk') {
          addLog(`✅ Subscribed (Tick): ${message.e}|${message.tk} (${message.ts})`, 'success');
        }

        // Depth acknowledgment
        if (message.t === 'dk') {
          addLog(`✅ Subscribed (Depth): ${message.e}|${message.tk}`, 'success');
        }

        // Tick feed (LTP update)
        if (message.t === 'tf') {
          const ltpInfo = `📈 ${message.e}|${message.tk} - LTP: ₹${message.lp || 'N/A'}`;
          addLog(ltpInfo, 'price');
        }

        // Depth feed
        if (message.t === 'df') {
          const key = `${message.e}|${message.tk}`;
          
          // Update depth data
          setDepthData(prev => {
            const existing = prev[key] || {};
            return {
              ...prev,
              [key]: {
                exchange: message.e,
                token: message.tk,
                tbq: message.tbq !== undefined ? message.tbq : existing.tbq,
                tsq: message.tsq !== undefined ? message.tsq : existing.tsq,
                lp: message.lp !== undefined ? message.lp : existing.lp,
                lastUpdate: new Date().toLocaleTimeString()
              }
            };
          });
          
          const tbq = message.tbq !== undefined ? message.tbq : '-';
          const tsq = message.tsq !== undefined ? message.tsq : '-';
          const lp = message.lp !== undefined ? message.lp : '-';
          addLog(`📊 Depth: ${key} | TBQ: ${tbq} | TSQ: ${tsq} | LTP: ${lp}`, 'price');
        }

        // Log raw message for debugging
        if (!['tf', 'tk', 'ck', 'df', 'dk'].includes(message.t)) {
          addLog(`📨 ${JSON.stringify(message)}`, 'debug');
        }
      };

      ws.onerror = (error) => {
        addLog(`❌ WebSocket error: ${error.message}`, 'error');
      };

      ws.onclose = () => {
        addLog('🔌 WebSocket disconnected', 'warning');
        setIsConnected(false);
      };

      // Heartbeat every 50 seconds
      const heartbeatInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ k: '', t: 'h' }));
          addLog('💓 Heartbeat sent', 'debug');
        }
      }, 50000);

      // Cleanup
      return () => {
        clearInterval(heartbeatInterval);
        if (ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
      };

    } catch (error) {
      addLog(`❌ Connection error: ${error.message}`, 'error');
    }
  };

  const subscribeToSymbols = () => {
    if (!isConnected || !wsRef.current) {
      addLog('❌ Not connected to WebSocket', 'error');
      return;
    }

    if (testSymbols.length === 0) {
      addLog('❌ No symbols to subscribe', 'error');
      return;
    }

    // Subscribe to DEPTH data for both CE and PE
    testSymbols.forEach((symbol) => {
      const message = {
        k: `${symbol.exchange}|${symbol.token}`,
        t: 'd' // 'd' for depth data
      };
      wsRef.current.send(JSON.stringify(message));
      addLog(`📊 Subscribed to DEPTH: ${symbol.symbol} (${symbol.exchange}|${symbol.token})`, 'success');
    });
  };

  const disconnect = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
      setIsConnected(false);
      addLog('Disconnected from WebSocket', 'info');
    }
  };

  const clearLogs = () => {
    setLogs([]);
  };

  const getLogColor = (type) => {
    switch (type) {
      case 'success': return 'text-green-600';
      case 'error': return 'text-red-600';
      case 'warning': return 'text-yellow-600';
      case 'price': return 'text-blue-600 font-semibold';
      case 'debug': return 'text-gray-500 text-sm';
      default: return 'text-gray-700';
    }
  };

  if (!userId) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">Please login first to test WebSocket</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">WebSocket Test</h1>
        <p className="text-gray-600">Test AliceBlue WebSocket connection and live market data</p>
      </div>

      {/* Connection Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 mb-1">User ID</div>
          <div className="font-semibold">{userId}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 mb-1">Connection Status</div>
          <div className={`font-semibold ${isConnected ? 'text-green-600' : 'text-red-600'}`}>
            {isConnected ? '🟢 Connected' : '🔴 Disconnected'}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 mb-1">WS Session</div>
          <div className="font-mono text-xs truncate">{wsSession || 'Not created'}</div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Controls</h2>
        
        <div className="space-y-4">
          <div className="flex gap-2">
            <button
              onClick={connectWebSocket}
              disabled={isConnected}
              className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isConnected ? 'Connected' : 'Connect WebSocket'}
            </button>
            <button
              onClick={disconnect}
              disabled={!isConnected}
              className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Disconnect
            </button>
            <button
              onClick={createWsSession}
              className="btn btn-secondary"
            >
              Create New Session
            </button>
          </div>
        </div>
      </div>

      {/* NFO Symbol Selection */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">NFO Symbol Selection</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Symbol Name</label>
            <select
              value={symbolName}
              onChange={(e) => setSymbolName(e.target.value)}
              className="input w-full"
              disabled={!isConnected}
            >
              <option value="">Select Symbol</option>
              <option value="NIFTY">NIFTY</option>
              <option value="BANKNIFTY">BANKNIFTY</option>
              <option value="FINNIFTY">FINNIFTY</option>
              <option value="MIDCPNIFTY">MIDCPNIFTY</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Date</label>
            <select
              value={selectedExpiry}
              onChange={(e) => setSelectedExpiry(e.target.value)}
              className="input w-full"
              disabled={!symbolName || !isConnected || availableExpiries.length === 0}
            >
              <option value="">Select Expiry</option>
              {availableExpiries.map((exp) => (
                <option key={exp.date} value={exp.date}>
                  {exp.formatted}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Strike Price</label>
            <input
              type="number"
              value={strikePrice}
              onChange={(e) => setStrikePrice(e.target.value)}
              placeholder="e.g., 26000"
              className="input w-full"
              disabled={!selectedExpiry || !isConnected}
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={fetchTestSymbols}
              disabled={!symbolName || !selectedExpiry || !strikePrice || !isConnected}
              className="btn btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Fetch Symbols
            </button>
          </div>
        </div>

        {/* Test Symbols Display */}
        {testSymbols.length > 0 && (
          <div className="mb-4">
            <div className="bg-gray-50 rounded border border-gray-200 p-4">
              <div className="text-sm font-semibold text-gray-700 mb-2">Selected Symbols:</div>
              {testSymbols.map((sym, idx) => (
                <div key={idx} className="text-sm text-gray-600 font-mono">
                  {sym.type}: {sym.symbol} ({sym.exchange}|{sym.token})
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={subscribeToSymbols}
            disabled={!isConnected || testSymbols.length === 0}
            className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Subscribe to Depth Data
          </button>
        </div>
      </div>

      {/* Depth Data Display */}
      {Object.keys(depthData).length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Live Depth Data</h2>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Symbol</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total Buy Qty</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total Sell Qty</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">LTP</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Update</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(depthData).map(([key, data]) => {
                  const symbol = testSymbols.find(s => `${s.exchange}|${s.token}` === key);
                  return (
                    <tr key={key}>
                      <td className="px-4 py-2 text-sm font-mono">{symbol?.symbol || key}</td>
                      <td className="px-4 py-2 text-sm">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          symbol?.type === 'CE' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {symbol?.type || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm text-right font-semibold text-green-600">
                        {data.tbq !== undefined ? data.tbq.toLocaleString() : '-'}
                      </td>
                      <td className="px-4 py-2 text-sm text-right font-semibold text-red-600">
                        {data.tsq !== undefined ? data.tsq.toLocaleString() : '-'}
                      </td>
                      <td className="px-4 py-2 text-sm text-right font-semibold">
                        {data.lp !== undefined ? `₹${data.lp}` : '-'}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600">{data.lastUpdate}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Logs */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Activity Log</h2>
          <button onClick={clearLogs} className="text-sm text-gray-600 hover:text-gray-800">
            Clear Logs
          </button>
        </div>
        
        <div
          ref={logContainerRef}
          className="bg-gray-50 rounded border border-gray-200 p-4 h-96 overflow-y-auto font-mono text-sm"
        >
          {logs.length === 0 ? (
            <div className="text-gray-500 text-center py-8">No activity yet. Click "Connect WebSocket" to start.</div>
          ) : (
            logs.map((log, index) => (
              <div key={index} className={`mb-1 ${getLogColor(log.type)}`}>
                <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Help */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">How to Use:</h3>
        <div className="text-sm text-blue-800 space-y-1">
          <div>1. Click "Connect WebSocket" to establish connection</div>
          <div>2. Select Symbol Name (NIFTY, BANKNIFTY, etc.)</div>
          <div>3. Select Expiry Date from dropdown</div>
          <div>4. Enter Strike Price (e.g., 26000)</div>
          <div>5. Click "Fetch Symbols" to find CE/PE options</div>
          <div>6. Click "Subscribe to Depth Data" to get live TBQ/TSQ values</div>
          <div>7. Watch the Live Depth Data table for real-time updates</div>
        </div>
        <div className="mt-3 pt-3 border-t border-blue-300">
          <div className="text-sm text-blue-800">
            <strong>Note:</strong> TBQ (Total Buy Quantity) and TSQ (Total Sell Quantity) update periodically. 
            AliceBlue sends abbreviated messages - values persist until new data arrives.
          </div>
        </div>
      </div>

      {/* Important Notice */}
      <div className="mt-6 bg-yellow-50 border border-yellow-300 rounded-lg p-4">
        <h3 className="font-semibold text-yellow-900 mb-2 flex items-center">
          <span className="mr-2">⚠️</span> Important: WebSocket Limitations
        </h3>
        <div className="text-sm text-yellow-800 space-y-2">
          <p>
            <strong>Getting HTTP 405 Error?</strong> This means AliceBlue's real-time WebSocket streaming 
            is not enabled for your account.
          </p>
          <p>
            AliceBlue WebSocket access typically requires:
          </p>
          <ul className="list-disc list-inside ml-2 space-y-1">
            <li>A paid subscription plan (not available on free tier)</li>
            <li>Special API permissions from AliceBlue</li>
            <li>Approval for streaming data access</li>
          </ul>
          <p>
            <strong>Alternative:</strong> The app uses REST API polling for market data, which works 
            without WebSocket. Real-time data will have a slight delay but is functional.
          </p>
          <p className="text-xs mt-2">
            Contact: <a href="mailto:support@aliceblueonline.com" className="underline">support@aliceblueonline.com</a> to enable WebSocket access.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WebSocketTest;
